import { request } from "./groups";

export type MediaKind = "photo" | "video" | "voice" | "file";
export type P2PShareKind = "image" | "video" | "voice" | "file" | "collab";
export type TeasePackCode = "spicy" | "adult";

export async function createMediaEnvelope(payload: {
  conversationId: string;
  mediaKind: MediaKind;
  transport?: "e2ee_upload";
  clientMessageId?: string;
  encryptedManifest: any;
  captionEnvelope?: any | null;
  sizeBytes?: number | null;
  mimeType?: string | null;
  durationMs?: number | null;
  width?: number | null;
  height?: number | null;
  expiresAt?: string | null;
}) {
  return request<any>("/messages/media-envelopes", {
    method: "POST",
    body: JSON.stringify({
      conversationId: payload.conversationId,
      mediaKind: payload.mediaKind,
      transport: payload.transport || "e2ee_upload",
      clientMessageId: payload.clientMessageId,
      encryptedManifest: payload.encryptedManifest,
      captionEnvelope: payload.captionEnvelope ?? null,
      sizeBytes: payload.sizeBytes ?? null,
      mimeType: payload.mimeType ?? null,
      durationMs: payload.durationMs ?? null,
      width: payload.width ?? null,
      height: payload.height ?? null,
      expiresAt: payload.expiresAt ?? null,
    }),
  });
}

export async function listMediaEnvelopes(conversationId: string) {
  const q = new URLSearchParams({ conversation_id: conversationId });
  return request<{ ok?: boolean; items?: any[] }>(`/messages/media-envelopes?${q.toString()}`);
}

export async function createP2PShare(payload: {
  conversationId: string;
  shareKind: P2PShareKind;
  originGroupId?: string | null;
  originActivityId?: string | null;
  meta?: any;
}) {
  return request<any>("/messages/p2p-shares", {
    method: "POST",
    body: JSON.stringify({
      conversationId: payload.conversationId,
      shareKind: payload.shareKind,
      originGroupId: payload.originGroupId || null,
      originActivityId: payload.originActivityId || null,
      meta: payload.meta || {},
    }),
  });
}

export async function addP2PShareSignal(
  shareId: string,
  payload: {
    signalType: "offer" | "answer" | "ice" | "accept" | "decline" | "cancel" | "complete";
    recipientUserId?: number | string | null;
    payload?: any;
  }
) {
  return request<any>(`/messages/p2p-shares/${encodeURIComponent(shareId)}/signals`, {
    method: "POST",
    body: JSON.stringify({
      signalType: payload.signalType,
      recipientUserId: payload.recipientUserId ?? null,
      payload: payload.payload || {},
    }),
  });
}

export async function listP2PShareSignals(shareId: string, afterId?: number) {
  const q = new URLSearchParams();
  if (afterId) q.set("afterId", String(afterId));
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return request<{ ok?: boolean; items?: any[] }>(
    `/messages/p2p-shares/${encodeURIComponent(shareId)}/signals${suffix}`
  );
}

export async function acceptP2PShare(shareId: string) {
  return request<any>(`/messages/p2p-shares/${encodeURIComponent(shareId)}/accept`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function listTeaseTemplates(groupId: string, packCode?: TeasePackCode) {
  const q = new URLSearchParams();
  if (packCode) q.set("packCode", packCode);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return request<{ ok?: boolean; items?: any[] }>(
    `/groups/${encodeURIComponent(groupId)}/teases${suffix}`
  );
}

export async function sendTeaseEvent(
  groupId: string,
  payload: {
    conversationId: string;
    templateId: string;
    packCode?: TeasePackCode;
    payloadIsEncrypted?: boolean;
    payloadContentType?: string;
    plaintextSummary?: string;
    encryptedPayloadJson?: any;
  }
) {
  return request<any>(`/groups/${encodeURIComponent(groupId)}/teases/send`, {
    method: "POST",
    body: JSON.stringify({
      conversationId: payload.conversationId,
      templateId: payload.templateId,
      packCode: payload.packCode || "spicy",
      payloadIsEncrypted: payload.payloadIsEncrypted,
      payloadContentType: payload.payloadContentType,
      plaintextSummary: payload.plaintextSummary,
      encryptedPayloadJson: payload.encryptedPayloadJson,
    }),
  });
}

export async function listTeaseEvents(groupId: string, limit = 10) {
  const q = new URLSearchParams();
  q.set("limit", String(limit));
  return request<{ ok?: boolean; items?: any[] }>(
    `/groups/${encodeURIComponent(groupId)}/teases/events?${q.toString()}`
  );
}
